import { getMetricFilterMapping, MetricFilterMapping } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { atomWithDebounce } from '@/atoms/debounce';
import { filtersAtom } from '@/atoms/filters/filter';
import { ConfigFilterAtomValues } from '@/types/userConfigs';

// scoped to the vis pages and hydrated from useMetricFilterMappingFeature (flags are hooks, so atoms can't read them)
export const metricFilterMappingEnabledAtom = atom(false);

export const mappedMetricFilterAtom = atom(get => {
  const { metric } = get(filtersAtom);
  return get(metricFilterMappingEnabledAtom) && metric ? getMetricFilterMapping(metric) : undefined;
});

export const isMetricMappedAtom = atom(get => get(mappedMetricFilterAtom) !== undefined);

// the mapping useApplyMetricFilterMapping last applied; kept after leaving the vis page, so coming back can tell whether
// the metric changed in the meantime
export const lastAppliedMappingAtom = atom<MetricFilterMapping | undefined>(undefined);

// the groups and zones the filters held when the vis page was left; if they are the same arrays on return, nothing
// replaced them in the meantime (a hydration, say), so what the user changed since the mapping was applied is kept
export type FiltersWhenLeft = Pick<ConfigFilterAtomValues, 'eventFilterGroups' | 'pitchStartZones' | 'pitchEndZones'>;
export const filtersWhenLeftAtom = atom<FiltersWhenLeft | undefined>(undefined);

export const appliedMappingMetricAtom = atom<string | null>(null);

// the same value, debounced in step with the event params (the player and team params hooks are the only writers), so
// it only flips once the params carry the mapping
export const mappedMetricReadyDebounceObject = atomWithDebounce<string | null>(null);
const { debouncedValueAtom: mappedMetricReadyAtom } = mappedMetricReadyDebounceObject;

export const mappedMetricKeyAtom = atom(get => (get(isMetricMappedAtom) ? get(filtersAtom).metric : undefined));

// a mapped metric whose filters aren't in the event params yet; the vis page shows its loading state until then,
// because mounting the data components now would request every event
export const isMappedMetricPendingAtom = atom(
  get => get(isMetricMappedAtom) && get(mappedMetricReadyAtom) !== get(filtersAtom).metric,
);
